// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

import * as React from 'react';

import clsx from 'clsx';

import {
  Point,
  FlowViewElement,
  ViewElement,
  StockViewElement,
  CloudViewElement,
  variableIsArrayed,
} from '@simlin/core/datamodel';
import { arrayWith, defined, Series } from '@simlin/core/common';
import { at, first, last } from '@simlin/core/collections';

import { Arrowhead } from './Arrowhead';
import { displayName, Point as IPoint, Rect } from './common';
import { AuxRadius, CloudRadius, FlowArrowheadRadius } from './default';
import { Label, labelBounds, LabelProps } from './Label';
import { Sparkline } from './Sparkline';
import { StockHeight, StockWidth } from './Stock';
import { jsFormatNumber as ff } from '../render-common';

import styles from './Flow.module.css';

type Side = 'left' | 'right' | 'top' | 'bottom';

/**
 * Classify a segment's working orientation for ROUTING decisions by its
 * dominant axis. Exact coordinate equality stays the definition of
 * "orthogonal" for rendering and normalization, but routing must also handle
 * slightly-diagonal data -- pre-fix creation bugs persisted flows with a few
 * pixels of cross-axis drift, and imported models can carry the same. Under
 * exact-equality checks such a visually-horizontal flow classified as
 * VERTICAL: stock drags routed a wrong-way L (valve and label stacked on the
 * source cloud) and endpoint drags constrained/updated the wrong coordinate.
 * Ties (a perfect 45-degree diagonal, only possible cloud-to-cloud) count as
 * horizontal.
 */
function isDominantlyHorizontal(p1: IPoint, p2: IPoint): boolean {
  return Math.abs(p2.y - p1.y) <= Math.abs(p2.x - p1.x);
}

/**
 * Returns a point on the edge of a stock.
 *
 * @param stockCx - X center of the stock
 * @param stockCy - Y center of the stock
 * @param side - Which side of the stock (left, right, top, bottom)
 * @param offsetFraction - Position along the edge, 0.5 = center (default), 0 = one end, 1 = other end.
 *                         For top/bottom: 0 = left, 1 = right.
 *                         For left/right: 0 = top, 1 = bottom.
 */
function getStockEdgePoint(stockCx: number, stockCy: number, side: Side, offsetFraction: number = 0.5): IPoint {
  switch (side) {
    case 'left':
      // Offset along Y axis: 0 = top, 1 = bottom
      return {
        x: stockCx - StockWidth / 2,
        y: stockCy - StockHeight / 2 + StockHeight * offsetFraction,
      };
    case 'right':
      // Offset along Y axis: 0 = top, 1 = bottom
      return {
        x: stockCx + StockWidth / 2,
        y: stockCy - StockHeight / 2 + StockHeight * offsetFraction,
      };
    case 'top':
      // Offset along X axis: 0 = left, 1 = right
      return {
        x: stockCx - StockWidth / 2 + StockWidth * offsetFraction,
        y: stockCy - StockHeight / 2,
      };
    case 'bottom':
      // Offset along X axis: 0 = left, 1 = right
      return {
        x: stockCx - StockWidth / 2 + StockWidth * offsetFraction,
        y: stockCy + StockHeight / 2,
      };
  }
}

/**
 * Which stock edge a flow endpoint currently occupies relative to a given stock
 * center, or undefined if the point is not on an edge (within tolerance).
 *
 * Used to preserve the attachment edge across a stock move when the
 * stock-adjacent segment runs PARALLEL to that edge -- a Z-shaped flow's riser
 * hugs a left/right edge with a vertical segment (or a top/bottom edge with a
 * horizontal one). Classifying by segment orientation alone would misread that
 * as the perpendicular edge and jump the endpoint on a mere nudge (#819
 * follow-up). Endpoints are pinned exactly onto edges, so a small tolerance is
 * safe and avoids float noise.
 */
function classifyStockEdge(point: IPoint, center: IPoint): Side | undefined {
  const EPS = 0.5;
  const dx = point.x - center.x;
  const dy = point.y - center.y;
  const onLeftRightEdge = Math.abs(Math.abs(dx) - StockWidth / 2) <= EPS && Math.abs(dy) <= StockHeight / 2 + EPS;
  const onTopBottomEdge = Math.abs(Math.abs(dy) - StockHeight / 2) <= EPS && Math.abs(dx) <= StockWidth / 2 + EPS;
  if (onLeftRightEdge) {
    return dx >= 0 ? 'right' : 'left';
  }
  if (onTopBottomEdge) {
    return dy >= 0 ? 'bottom' : 'top';
  }
  return undefined;
}

/**
 * Determine which stock edge a multi-segment (3+ point) flow's stock endpoint
 * attaches to after a stock move, plus whether the stock-adjacent segment is
 * horizontal. Shared by computeFlowRoute (which re-pins the endpoint) and
 * getFlowAttachmentInfo (which groups flows for spread spacing) so the two
 * ALWAYS agree on a Z-shaped flow's occupied edge -- otherwise a Z and a
 * straight flow on the same stock edge land in different spread groups and both
 * center at 0.5, overlapping their endpoints.
 *
 * Normally the segment orientation implies the edge (a horizontal segment
 * leaves a left/right edge). A Z-riser runs PARALLEL to its edge, so when we
 * know the prior stock center and the endpoint sits on an edge whose axis is
 * parallel to the segment, preserve that occupied edge instead.
 */
function resolveStockAdjacentSide(
  currentStockPoint: IPoint,
  adjacentPoint: IPoint,
  newStockCx: number,
  newStockCy: number,
  prevStockCenter: IPoint | undefined,
): { side: Side; isHorizontalSegment: boolean } {
  const isHorizontalSegment = isDominantlyHorizontal(currentStockPoint, adjacentPoint);

  if (prevStockCenter) {
    const occupiedEdge = classifyStockEdge(currentStockPoint, prevStockCenter);
    if (occupiedEdge) {
      const edgeIsLeftRight = occupiedEdge === 'left' || occupiedEdge === 'right';
      if (edgeIsLeftRight !== isHorizontalSegment) {
        return { side: occupiedEdge, isHorizontalSegment };
      }
    }
  }

  const side: Side = isHorizontalSegment
    ? adjacentPoint.x > newStockCx
      ? 'right'
      : 'left'
    : adjacentPoint.y > newStockCy
      ? 'bottom'
      : 'top';
  return { side, isHorizontalSegment };
}

interface FlowAttachmentInfo {
  flow: FlowViewElement;
  side: Side;
  // The anchor point (cloud/other stock at the opposite end from this stock)
  // Used for ordering flows on the same side - anchor position determines
  // where the flow is "going to", which is stable across L-shape creation
  anchor: IPoint;
  // Whether the stock is at the start (first point) or end (last point) of the flow
  stockIsFirst: boolean;
  // Whether the flow will be straight (vs L-shaped) - straight flows don't need spreading
  isStraight: boolean;
}

/**
 * Returns the point adjacent to the stock attachment (the point right next to where
 * the flow attaches to the stock).
 */
function getStockAdjacentPoint(points: readonly Point[], stockIsFirst: boolean): Point {
  const firstPoint = first(points);
  const lastPoint = last(points);
  if (stockIsFirst) {
    return points.length >= 2 ? at(points, 1) : lastPoint;
  } else {
    return points.length >= 2 ? at(points, points.length - 2) : firstPoint;
  }
}

/**
 * Returns the point adjacent to the anchor (used to determine the original flow direction).
 */
function getAnchorAdjacentPoint(points: readonly Point[], stockIsFirst: boolean): Point {
  const firstPoint = first(points);
  const lastPoint = last(points);
  if (stockIsFirst) {
    // anchor is last, so adjacent to anchor is second-to-last
    return points.length >= 2 ? at(points, points.length - 2) : firstPoint;
  } else {
    // anchor is first, so adjacent to anchor is second
    return points.length >= 2 ? at(points, 1) : lastPoint;
  }
}

/**
 * Determines which side of the stock a flow will attach to after the stock moves,
 * and returns the adjacent point for ordering purposes.
 *
 * This mirrors the logic in computeFlowRoute but extracts just the side determination.
 */
function getFlowAttachmentInfo(
  flow: FlowViewElement,
  stockUid: number,
  newStockCx: number,
  newStockCy: number,
  prevStockCenter?: IPoint,
): FlowAttachmentInfo | undefined {
  const points = flow.points;
  if (points.length < 2) {
    return undefined;
  }

  const firstPoint = first(points);
  const lastPoint = last(points);

  const stockIsFirst = firstPoint.attachedToUid === stockUid;
  const stockIsLast = lastPoint.attachedToUid === stockUid;

  if (!stockIsFirst && !stockIsLast) {
    return undefined;
  }

  const anchor = stockIsFirst ? lastPoint : firstPoint;
  const adjacentPoint = getStockAdjacentPoint(points, stockIsFirst);
  const anchorAdjacentPoint = getAnchorAdjacentPoint(points, stockIsFirst);
  const originalFlowIsHorizontal = isDominantlyHorizontal(anchorAdjacentPoint, anchor);

  let side: Side;
  let isStraight = false;

  // For 4+ point flows, use the existing segment orientation (preserving a
  // Z-riser's occupied edge -- see resolveStockAdjacentSide -- so this agrees
  // with computeFlowRoute and Z flows spread alongside straight ones).
  if (points.length >= 4) {
    const currentStockPoint = stockIsFirst ? firstPoint : lastPoint;
    ({ side } = resolveStockAdjacentSide(currentStockPoint, adjacentPoint, newStockCx, newStockCy, prevStockCenter));
  } else if (canFlowBeStraight(newStockCx, newStockCy, anchor.x, anchor.y, originalFlowIsHorizontal)) {
    // Straight flow - these naturally separate based on anchor position
    isStraight = true;
    if (originalFlowIsHorizontal) {
      side = anchor.x > newStockCx ? 'right' : 'left';
    } else {
      side = anchor.y > newStockCy ? 'bottom' : 'top';
    }
  } else {
    // L-shaped flow: attach perpendicular to the original flow direction
    if (originalFlowIsHorizontal) {
      side = anchor.y < newStockCy ? 'top' : 'bottom';
    } else {
      side = anchor.x < newStockCx ? 'left' : 'right';
    }
  }

  return {
    flow,
    side,
    anchor: { x: anchor.x, y: anchor.y },
    stockIsFirst,
    isStraight,
  };
}

/**
 * Groups flows by their attachment side and computes offset fractions for each flow.
 *
 * All flows on the same side are included in the spacing calculation to avoid overlap.
 * Flows are ordered by their anchor's position:
 * - For top/bottom sides: ordered by anchor's X (left to right)
 * - For left/right sides: ordered by anchor's Y (top to bottom)
 *
 * Straight flows get offset 0.5 (their position is determined by anchor, not offset),
 * but they're still included in the count so L-shaped flows spread around them.
 *
 * Using anchor position (not the stock-adjacent corner) ensures stable ordering
 * even for pre-existing L-shaped flows where corners may have the same coordinate.
 *
 * Returns a map from flow UID to its offset fraction (0 to 1).
 */
export function computeFlowOffsets(
  flows: readonly FlowViewElement[],
  stockUid: number,
  newStockCx: number,
  newStockCy: number,
  prevStockCenter?: IPoint,
): Map<number, number> {
  // Get attachment info for all flows
  const attachmentInfos: FlowAttachmentInfo[] = [];
  for (const flow of flows) {
    const info = getFlowAttachmentInfo(flow, stockUid, newStockCx, newStockCy, prevStockCenter);
    if (info) {
      attachmentInfos.push(info);
    }
  }

  // Group ALL flows by side (including straight flows for proper spacing)
  const bySide: Map<Side, FlowAttachmentInfo[]> = new Map();
  for (const info of attachmentInfos) {
    const existing = bySide.get(info.side) || [];
    existing.push(info);
    bySide.set(info.side, existing);
  }

  // Compute offsets for each flow
  const offsets: Map<number, number> = new Map();

  for (const [side, infos] of bySide) {
    if (infos.length === 1) {
      // Single flow on this side - center it (offset = 0.5)
      offsets.set(infos[0].flow.uid, 0.5);
    } else {
      // Multiple flows - sort all by anchor position and spread evenly.
      // Including straight flows in the count ensures L-shaped flows spread
      // around them, avoiding overlap even when straight flows' anchor positions
      // happen to coincide with spread positions.
      if (side === 'top' || side === 'bottom') {
        infos.sort((a, b) => a.anchor.x - b.anchor.x);
      } else {
        infos.sort((a, b) => a.anchor.y - b.anchor.y);
      }

      // Spread flows evenly: for n flows, positions are at 1/(n+1), 2/(n+1), ..., n/(n+1)
      const n = infos.length;
      for (let i = 0; i < n; i++) {
        const fraction = (i + 1) / (n + 1);
        if (infos[i].isStraight) {
          // Straight flows must use 0.5 - their position is determined by anchor,
          // not by offset. But including them in the count reserves their "slot".
          offsets.set(infos[i].flow.uid, 0.5);
        } else {
          offsets.set(infos[i].flow.uid, fraction);
        }
      }
    }
  }

  return offsets;
}

function canFlowBeStraight(
  stockCx: number,
  stockCy: number,
  anchorX: number,
  anchorY: number,
  originalFlowIsHorizontal: boolean,
): boolean {
  if (originalFlowIsHorizontal) {
    return Math.abs(stockCy - anchorY) <= StockHeight / 2;
  } else {
    return Math.abs(stockCx - anchorX) <= StockWidth / 2;
  }
}

// Exported for testing
export function computeFlowRoute(
  flow: FlowViewElement,
  stockEl: StockViewElement,
  newStockCx: number,
  newStockCy: number,
  offsetFraction: number = 0.5,
  prevStockCenter?: IPoint,
): FlowViewElement {
  const points = flow.points;
  if (points.length < 2) {
    return flow;
  }

  const firstPoint = first(points);
  const lastPoint = last(points);

  const stockIsFirst = firstPoint.attachedToUid === stockEl.uid;
  const stockIsLast = lastPoint.attachedToUid === stockEl.uid;

  if (!stockIsFirst && !stockIsLast) {
    return flow;
  }

  const anchor = stockIsFirst ? lastPoint : firstPoint;

  // Determine original flow direction by looking at the anchor-side segment.
  // This works for both 2-point (straight) and 3+ point (L-shaped) flows.
  // For L-shaped flows, the anchor-side segment preserves the original direction.
  const anchorAdjacentPoint = getAnchorAdjacentPoint(points, stockIsFirst);
  const originalFlowIsHorizontal = isDominantlyHorizontal(anchorAdjacentPoint, anchor);

  // For flows with 4+ points (imported or manually-edited multi-segment flows),
  // update both the attached endpoint and the adjacent corner to preserve
  // orthogonality. The endpoint stays on the stock edge, and the adjacent
  // corner is adjusted to maintain axis alignment.
  if (points.length >= 4) {
    const adjacentPointIndex = stockIsFirst ? 1 : points.length - 2;
    const adjacentPoint = at(points, adjacentPointIndex);

    // Determine the ORIGINAL first segment's orientation from the existing
    // geometry (preserved to avoid diagonal segments between corner1 and corner2
    // when the stock moves far perpendicular to the segment) and the attachment
    // side, which also preserves a Z-riser's occupied edge to avoid an
    // endpoint jump on a nudge (see resolveStockAdjacentSide).
    const currentStockPoint = stockIsFirst ? firstPoint : lastPoint;
    const { side, isHorizontalSegment } = resolveStockAdjacentSide(
      currentStockPoint,
      adjacentPoint,
      newStockCx,
      newStockCy,
      prevStockCenter,
    );

    // Keep the endpoint on the stock's actual edge
    const stockEdge = getStockEdgePoint(newStockCx, newStockCy, side, offsetFraction);

    const newStockPoint: Point = {
      x: stockEdge.x,
      y: stockEdge.y,
      attachedToUid: stockEl.uid,
    };

    // Adjust the adjacent corner to preserve orthogonality
    let newAdjacentPoint: Point;
    if (isHorizontalSegment) {
      // For horizontal segment, corner's Y must match endpoint's Y
      newAdjacentPoint = {
        x: adjacentPoint.x,
        y: stockEdge.y,
        attachedToUid: adjacentPoint.attachedToUid,
      };
    } else {
      // For vertical segment, corner's X must match endpoint's X
      newAdjacentPoint = {
        x: stockEdge.x,
        y: adjacentPoint.y,
        attachedToUid: adjacentPoint.attachedToUid,
      };
    }

    let newPoints: readonly Point[];
    if (stockIsFirst) {
      newPoints = arrayWith(arrayWith(points, 0, newStockPoint), 1, newAdjacentPoint);
    } else {
      newPoints = arrayWith(arrayWith(points, points.length - 1, newStockPoint), points.length - 2, newAdjacentPoint);
    }

    // Preserve valve's fractional position on the stock-adjacent segment.
    // When a stock moves along the flow axis, the segment gets longer or shorter.
    // If the valve is on that segment, we should preserve its fractional position
    // (similar to how straight flows are handled) rather than just clamping its
    // absolute position, which would cause it to slide or jump.
    const currentValve: IPoint = { x: flow.x, y: flow.y };
    const oldSegments = getSegments(points);
    const valveOldSegment = findClosestSegment(currentValve, oldSegments);
    const stockAdjacentSegmentIndex = stockIsFirst ? 0 : oldSegments.length - 1;
    const valveIsOnStockAdjacentSegment = valveOldSegment.index === stockAdjacentSegmentIndex;

    // Normalize to remove any colinear or zero-length segments that may have
    // been created by adjusting the adjacent corner.
    newPoints = normalizeFlowPoints(newPoints);

    const newSegments = getSegments(newPoints);

    let newValve: IPoint;
    if (valveIsOnStockAdjacentSegment && newSegments.length > 0) {
      // Valve is on the stock-adjacent segment - preserve fractional position.
      const newStockAdjacentSegmentIndex = stockIsFirst ? 0 : newSegments.length - 1;
      const newStockAdjacentSegment = newSegments[newStockAdjacentSegmentIndex];
      newValve = preserveValveFraction(currentValve, valveOldSegment, newStockAdjacentSegment);
    } else {
      // Valve is not on the stock-adjacent segment - clamp to closest segment.
      const closestSegment = findClosestSegment(currentValve, newSegments);
      newValve = clampToSegment(currentValve, closestSegment);
    }

    return {
      ...flow,
      x: newValve.x,
      y: newValve.y,
      points: newPoints,
    };
  }

  if (canFlowBeStraight(newStockCx, newStockCy, anchor.x, anchor.y, originalFlowIsHorizontal)) {
    let stockEdge: IPoint;
    if (originalFlowIsHorizontal) {
      const side: Side = anchor.x > newStockCx ? 'right' : 'left';
      stockEdge = getStockEdgePoint(newStockCx, anchor.y, side, offsetFraction);
    } else {
      const side: Side = anchor.y > newStockCy ? 'bottom' : 'top';
      stockEdge = getStockEdgePoint(anchor.x, newStockCy, side, offsetFraction);
    }

    const newStockPoint: Point = {
      x: stockEdge.x,
      y: stockEdge.y,
      attachedToUid: stockEl.uid,
    };

    let newPoints: readonly Point[];
    if (stockIsFirst) {
      newPoints = [newStockPoint, anchor];
    } else {
      newPoints = [firstPoint, newStockPoint];
    }

    // Preserve valve's fractional position along the segment.
    // This prevents the valve from jumping when the stock moves past its position.
    // Find which segment the valve was actually on (for L-shaped flows, the valve
    // might be on either segment, so we can't just assume it was on segment 0).
    const currentValve: IPoint = { x: flow.x, y: flow.y };
    const oldSegments = getSegments(points);
    const newSegments = getSegments(newPoints);
    const valveSegment = findClosestSegment(currentValve, oldSegments);
    const newValve = preserveValveFraction(currentValve, valveSegment, newSegments[0]);

    return {
      ...flow,
      x: newValve.x,
      y: newValve.y,
      points: newPoints,
    };
  }

  // For L-shaped flow, attach perpendicular to the original flow direction
  let attachmentSide: Side;
  if (originalFlowIsHorizontal) {
    // Original was horizontal, so the new segment from stock should be vertical
    attachmentSide = anchor.y < newStockCy ? 'top' : 'bottom';
  } else {
    // Original was vertical, so the new segment from stock should be horizontal
    attachmentSide = anchor.x < newStockCx ? 'left' : 'right';
  }
  const stockEdge = getStockEdgePoint(newStockCx, newStockCy, attachmentSide, offsetFraction);

  // Corner connects the stock's perpendicular segment to the original flow direction
  let corner: IPoint;
  if (originalFlowIsHorizontal) {
    // Vertical segment from stock, then horizontal to anchor
    corner = { x: stockEdge.x, y: anchor.y };
  } else {
    // Horizontal segment from stock, then vertical to anchor
    corner = { x: anchor.x, y: stockEdge.y };
  }

  const newStockPoint: Point = {
    x: stockEdge.x,
    y: stockEdge.y,
    attachedToUid: stockEl.uid,
  };
  const cornerPoint: Point = {
    x: corner.x,
    y: corner.y,
    attachedToUid: undefined,
  };

  let newPoints: readonly Point[];
  if (stockIsFirst) {
    newPoints = [newStockPoint, cornerPoint, anchor];
  } else {
    newPoints = [firstPoint, cornerPoint, newStockPoint];
  }

  // Preserve valve position by clamping to the closest segment of the new L-shape
  const currentValve: IPoint = { x: flow.x, y: flow.y };
  const clampedValve = clampValveToClosestSegment(currentValve, newPoints);

  return {
    ...flow,
    x: clampedValve.x,
    y: clampedValve.y,
    points: newPoints,
  };
}

function adjustFlows(
  origStock: StockViewElement | CloudViewElement,
  stock: StockViewElement | CloudViewElement,
  flows: readonly FlowViewElement[],
): readonly FlowViewElement[] {
  return flows.map((flow: FlowViewElement) => {
    let horizontal = isHorizontal(flow);
    const vertical = isVertical(flow);
    const inCreation = horizontal && vertical;
    if (!inCreation && !horizontal && !vertical && flow.points.length === 2) {
      // Slightly-diagonal legacy data: neither exact test matched, so classify
      // by the dominant axis (see isDominantlyHorizontal). Without this, a
      // visually-horizontal drifted flow snapped the dragged endpoint's y but
      // never updated its x, detaching the flow from its own endpoint.
      horizontal = isDominantlyHorizontal(first(flow.points), last(flow.points));
    }

    let otherEnd: IPoint | undefined;
    const points = flow.points.map((point, i) => {
      // if its not the start or end point, don't change it.
      if (!(i === 0 || i === flow.points.length - 1)) {
        return point;
      }

      if (point.attachedToUid !== stock.uid) {
        otherEnd = point;
        return point;
      }

      let compare: IPoint;
      // we're inside a lambda passed to flow.points.map(), so while
      // first and last can conceptually return undefined, we know
      // that can't actually happen here.
      if (i === 0) {
        compare = flow.points[flow.points.length - 1] as IPoint;
      } else {
        compare = flow.points[0] as IPoint;
      }

      const d = {
        x: stock.x - compare.x,
        y: stock.y - compare.y,
      };

      if (inCreation) {
        // The flow is degenerate (all points coincide), so its own orientation
        // can't pick the axis -- recover it from the larger-magnitude component
        // of the source->target delta. A signed `d.x > d.y` here silently left
        // leftward/upward creations degenerate: the perpendicular component is
        // zeroed upstream, so the signed test collapsed to `d.x > 0` / `0 > d.y`,
        // which is wrong for the negative direction on each axis.
        horizontal = Math.abs(d.x) > Math.abs(d.y);
      }

      const adjust = {
        x: StockWidth / 2,
        y: StockHeight / 2,
      };
      if (stock.type === 'cloud' || stock.isZeroRadius) {
        adjust.x = 0;
        adjust.y = 0;
      }

      if (horizontal && d.x < 0) {
        // right
        point = { ...point, x: stock.x + adjust.x };
      } else if (horizontal) {
        // left
        point = { ...point, x: stock.x - adjust.x };
      } else if (!horizontal && d.y < 0) {
        // bottom
        point = { ...point, y: stock.y + adjust.y };
      } else {
        // top
        point = { ...point, y: stock.y - adjust.y };
      }

      return point;
    });

    // Degenerate self-loop: both endpoints attach to the same stock, so no
    // endpoint was recorded as the "other end" to anchor the valve fraction
    // against. Such a flow can't be created in the editor but can arrive via an
    // imported model or a programmatic patch (issue #720). Leave it unchanged
    // rather than throwing on the canvas render/interaction path.
    if (otherEnd === undefined) {
      return flow;
    }

    // For multi-segment flows (3+ points), use segment-based valve positioning.
    // When moving an endpoint (like the arrowhead), the valve should only be
    // affected if it's on the segment adjacent to that endpoint. If it's on
    // an interior segment, it should stay put.
    if (points.length >= 3) {
      const currentValve: IPoint = { x: flow.x, y: flow.y };
      const oldSegments = getSegments(flow.points);
      const newSegments = getSegments(points);
      const valveOldSegment = findClosestSegment(currentValve, oldSegments);

      // Determine which endpoint is being moved (attached to stock)
      const stockIsFirst = first(flow.points).attachedToUid === stock.uid;
      const movedEndpointSegmentIndex = stockIsFirst ? 0 : oldSegments.length - 1;

      let newValve: IPoint;
      if (valveOldSegment.index === movedEndpointSegmentIndex && newSegments.length > 0) {
        // Valve is on the segment adjacent to the moved endpoint - preserve
        // fractional position on that segment (similar to computeFlowRoute)
        const newSegment = newSegments[stockIsFirst ? 0 : newSegments.length - 1];
        newValve = preserveValveFraction(currentValve, valveOldSegment, newSegment);
      } else {
        // Valve is on an interior segment that wasn't affected by the endpoint
        // move - keep it in place
        newValve = currentValve;
      }

      return {
        ...flow,
        x: newValve.x,
        y: newValve.y,
        points,
      };
    }

    // Re-place the valve by preserving its FRACTIONAL position along the segment,
    // anchored to the fixed `otherEnd`. The earlier form -- base = min(otherEnd,
    // moved end) plus the ABSOLUTE scaled offset -- happened to be idempotent only
    // when otherEnd was the SMALLER coordinate (a sink cloud on the right). When
    // the dragged end was the smaller coordinate (a SOURCE cloud on the left),
    // `base` landed on the moved end and the offset-from-otherEnd was applied from
    // the wrong reference, reflecting an off-center valve across the segment
    // midpoint on the very first drag frame (#832). Anchoring to otherEnd with a
    // SIGNED new length keeps the valve between the two ends even when the moved
    // end crosses past otherEnd (the flip case the mirror form was chasing), and
    // clamping the fraction to [0, 1] keeps it on the segment. This mirrors what
    // `preserveValveFraction` already does for multi-segment flows.
    //
    // The `oldLen === 0` branch preserves the #818 divide-by-zero guard and has
    // two sub-cases distinguished by the NEW length:
    //   - newLen === 0: the axis is perpendicular to the flow (x for a vertical
    //     flow, y for a horizontal one) -- origStock and otherEnd share a
    //     coordinate and still do, so there is no length to take a fraction of and
    //     the valve stays on otherEnd's coordinate (frac 0).
    //   - newLen !== 0: the OLD flow was DEGENERATE (a creation flow whose points
    //     all coincided at the press point) and this end has now expanded out;
    //     center the valve on the freshly-created segment (frac 0.5), matching the
    //     prior formula's degenerate fallback so creation keeps its mid-segment
    //     valve.
    const preserveAxis = (valve: number, other: number, origMoved: number, newMoved: number): number => {
      const oldLen = origMoved - other;
      const newLen = newMoved - other;
      const frac = oldLen === 0 ? (newLen === 0 ? 0 : 0.5) : Math.max(0, Math.min(1, (valve - other) / oldLen));
      return other + frac * newLen;
    };
    flow = {
      ...flow,
      x: preserveAxis(flow.x, otherEnd.x, origStock.x, stock.x),
      y: preserveAxis(flow.y, otherEnd.y, origStock.y, stock.y),
    };

    return { ...flow, points };
  });
}

/**
 * Pin a flow's stock-attached SOURCE endpoint onto the stock edge facing the
 * sink, preserving the flow's orientation (the perpendicular coordinate is
 * untouched, so a straight flow stays straight) and re-clamping the valve to
 * the resulting path. Used at flow-creation commit: the flow tool stages the
 * source point at the press-time stock CENTER and only routes the sink, so
 * without this the persisted source endpoint sits hidden under the stock body
 * (violating the edge-attachment rule) until the next stock drag re-pins it.
 */
export function pinSourceToStockEdge(flow: FlowViewElement, stock: StockViewElement): FlowViewElement {
  if (flow.points.length < 2) {
    return flow;
  }
  const srcPt = first(flow.points);
  const sinkPt = last(flow.points);
  if (srcPt.attachedToUid !== stock.uid) {
    return flow;
  }

  const horizontal = isDominantlyHorizontal(srcPt, sinkPt);
  const side: Side = horizontal ? (sinkPt.x >= stock.x ? 'right' : 'left') : sinkPt.y >= stock.y ? 'bottom' : 'top';
  const edge = getStockEdgePoint(stock.x, stock.y, side);
  const newSrc: Point = horizontal ? { ...srcPt, x: edge.x } : { ...srcPt, y: edge.y };
  const points: readonly Point[] = [newSrc, ...flow.points.slice(1)];

  const currentValve: IPoint = { x: flow.x, y: flow.y };
  const segments = getSegments(points);
  const valve = clampToSegment(currentValve, findClosestSegment(currentValve, segments));
  return { ...flow, x: valve.x, y: valve.y, points };
}

export function UpdateStockAndFlows(
  stockEl: StockViewElement,
  flows: readonly FlowViewElement[],
  moveDelta: IPoint,
): [StockViewElement, readonly FlowViewElement[]] {
  const newStockCx = stockEl.x - moveDelta.x;
  const newStockCy = stockEl.y - moveDelta.y;

  // The stock's center before this move, used to classify which edge each flow
  // endpoint currently occupies so computeFlowRoute can preserve it (see the
  // Z-riser edge-preservation there).
  const prevStockCenter: IPoint = { x: stockEl.x, y: stockEl.y };

  stockEl = {
    ...stockEl,
    x: newStockCx,
    y: newStockCy,
  };

  // Compute offset fractions to spread multiple flows on the same side
  const offsets = computeFlowOffsets(flows, stockEl.uid, newStockCx, newStockCy, prevStockCenter);

  flows = flows.map((flow) => {
    const offset = offsets.get(flow.uid) ?? 0.5;
    return computeFlowRoute(flow, stockEl, newStockCx, newStockCy, offset, prevStockCenter);
  });

  return [stockEl, flows];
}

function allEqual<T>(extractor: (pt: Point) => T): (flow: FlowViewElement) => boolean {
  return (flow: FlowViewElement) => {
    if (flow.points.length === 0) {
      return false;
    }

    const firstVal = extractor(at(flow.points, 0));
    return flow.points.every((pt) => extractor(pt) === firstVal);
  };
}

const isHorizontal = allEqual((pt) => pt.y);
const isVertical = allEqual((pt) => pt.x);

export function UpdateCloudAndFlow(
  cloud: StockViewElement | CloudViewElement,
  flow: FlowViewElement,
  moveDelta: IPoint,
): [StockViewElement | CloudViewElement, FlowViewElement] {
  let points = flow.points;
  const firstPoint = first(points);
  const lastPoint = last(points);

  // Determine if cloud is at first or last position
  const cloudIsFirst = firstPoint.attachedToUid === cloud.uid;
  const cloudIsLast = lastPoint.attachedToUid === cloud.uid;

  if (!cloudIsFirst && !cloudIsLast) {
    return [cloud, flow];
  }

  const segments = getSegments(points);
  const currentValve: IPoint = { x: flow.x, y: flow.y };

  // For 2-point (straight) flows, check for perpendicular offset
  if (segments.length === 1) {
    const seg = segments[0];

    // Degenerate segment: both points at same position (e.g., during flow creation).
    // In this case, seg.isHorizontal and seg.isVertical are both true.
    // Use the drag direction to determine the intended axis.
    const isDegenerate = seg.isHorizontal && seg.isVertical;

    // For degenerate segments, determine axis from drag direction. For
    // non-degenerate segments, use the segment's DOMINANT orientation (see
    // isDominantlyHorizontal): an exact check misread a parallel drag of a
    // slightly-diagonal legacy flow as perpendicular and rerouted it into a
    // bogus L.
    const treatAsHorizontal = isDegenerate
      ? Math.abs(moveDelta.x) > Math.abs(moveDelta.y)
      : isDominantlyHorizontal(seg.p1, seg.p2);

    const perpDelta = treatAsHorizontal ? moveDelta.y : moveDelta.x;
    const parDelta = treatAsHorizontal ? moveDelta.x : moveDelta.y;

    const PERP_THRESHOLD = 5;
    const perpAbs = Math.abs(perpDelta);
    const parAbs = Math.abs(parDelta);
    // Don't reroute degenerate flows - they should stay straight
    const shouldReroute = !isDegenerate && perpAbs >= PERP_THRESHOLD && perpAbs > parAbs;

    if (shouldReroute) {
      // Bend the straight flow into an L by inserting a corner. The dragged cloud
      // moves by the FULL accumulated delta -- BOTH the perpendicular component
      // (which forms the bend) AND the parallel component. An earlier version
      // applied only the perpendicular delta and kept the cloud's ORIGINAL
      // parallel coordinate; because the live drag re-routes from the original
      // 2-point flow each pointermove, a drag that had already traveled
      // along-axis snapped the cloud back to its starting parallel position the
      // instant it crossed the perpendicular threshold (a one-frame teleport).
      const base = cloudIsFirst ? firstPoint : lastPoint;
      const otherPoint = cloudIsFirst ? lastPoint : firstPoint;
      const newCloudPoint: Point = { ...base, x: base.x - moveDelta.x, y: base.y - moveDelta.y };

      // The corner keeps the two segments orthogonal: a riser on the FIXED end's
      // coordinate, then a run out to the cloud. For a (dominantly) horizontal
      // flow the riser is vertical at otherPoint.x; for a vertical flow it is
      // horizontal at otherPoint.y.
      const corner: Point = treatAsHorizontal
        ? { x: otherPoint.x, y: newCloudPoint.y, attachedToUid: undefined }
        : { x: newCloudPoint.x, y: otherPoint.y, attachedToUid: undefined };

      points = cloudIsFirst ? [newCloudPoint, corner, otherPoint] : [otherPoint, corner, newCloudPoint];

      // Update cloud position to the full dragged position.
      cloud = {
        ...cloud,
        x: newCloudPoint.x,
        y: newCloudPoint.y,
      };

      // Normalize before placing the valve: if the cloud was dragged onto the
      // fixed end's axis the run collapses to zero length and the L degenerates
      // to a straight flow -- normalizeFlowPoints drops the redundant corner so
      // the valve lands on a real segment.
      points = normalizeFlowPoints(points);

      // Land the valve on the segment adjacent to the dragged cloud -- the bent
      // segment the drag is actively growing -- at an interior position. Picking
      // the merely-CLOSEST segment here (clampValveToClosestSegment) let the
      // valve's closest segment flip from this bent segment onto the
      // perpendicular riser as the L deepened, teleporting the valve (and its
      // label) to the riser's stock-adjacent end, flush against the stock body
      // (#53). Anchoring to the cloud-adjacent segment tracks the cursor
      // continuously, and clampToSegment keeps the valve off the segment ends.
      const rerouteSegments = getSegments(points);
      const cloudAdjacentSegment = cloudIsFirst ? rerouteSegments[0] : rerouteSegments[rerouteSegments.length - 1];
      const newValve = clampToSegment(currentValve, cloudAdjacentSegment);

      flow = {
        ...flow,
        x: newValve.x,
        y: newValve.y,
        points,
      };

      return [cloud, flow];
    }

    // No perpendicular offset: constrain to flow axis
    let proposed: Point = {
      x: cloud.x - moveDelta.x,
      y: cloud.y - moveDelta.y,
      attachedToUid: cloud.uid,
    };

    if (treatAsHorizontal) {
      proposed = { ...proposed, y: firstPoint.y };
    } else {
      proposed = { ...proposed, x: firstPoint.x };
    }

    const origCloud = cloud;
    cloud = {
      ...cloud,
      x: proposed.x,
      y: proposed.y,
    };

    flow = first(adjustFlows(origCloud, cloud, [flow]));
    return [cloud, flow];
  }

  // For multi-segment flows: update adjacent corner to maintain orthogonality
  const cloudPointIndex = cloudIsFirst ? 0 : points.length - 1;
  const adjacentPointIndex = cloudIsFirst ? 1 : points.length - 2;
  const cloudPoint = at(points, cloudPointIndex);
  const adjacentPoint = at(points, adjacentPointIndex);

  // Determine segment orientation between cloud and adjacent point
  const adjacentSegment = cloudIsFirst ? segments[0] : segments[segments.length - 1];

  let newCloudX: number;
  let newCloudY: number;

  // For stocks, we need to determine whether to:
  // 1. Translate the endpoint (for normal movement where the edge stays correct), or
  // 2. Recompute the edge (when reattaching to a stock on the opposite side of the corner)
  //
  // The key insight is: if translating the old endpoint would put it on the WRONG side
  // of the stock center (opposite to where the corner is), we need to recompute the edge.
  //
  // IMPORTANT: Callers pass the stock with OLD coordinates and moveDelta = oldCenter - newCenter.
  // So the NEW stock center is: cloud.x - moveDelta.x, cloud.y - moveDelta.y
  //
  // Skip this branch for isZeroRadius stocks - these are temporary drag placeholders that
  // Canvas creates when detaching a flow from a stock. They should be treated as clouds.
  if (cloud.type === 'stock' && !cloud.isZeroRadius) {
    const isHorizontalSegment = adjacentSegment.isHorizontal;

    // Compute the NEW stock center (where it's being moved/reattached to)
    const newStockCx = cloud.x - moveDelta.x;
    const newStockCy = cloud.y - moveDelta.y;

    // Calculate what the translated endpoint position would be
    const translatedX = cloudPoint.x - moveDelta.x;
    const translatedY = cloudPoint.y - moveDelta.y;

    // Determine which side the adjacent point is on relative to the NEW stock center
    // This tells us which edge the endpoint SHOULD be on
    let expectedSide: Side;
    if (isHorizontalSegment) {
      expectedSide = adjacentPoint.x > newStockCx ? 'right' : 'left';
    } else {
      expectedSide = adjacentPoint.y > newStockCy ? 'bottom' : 'top';
    }

    // Check if the translated endpoint is on the correct side of the NEW stock center
    let translatedIsCorrectSide: boolean;
    if (isHorizontalSegment) {
      const shouldBeRight = expectedSide === 'right';
      const translatedIsRight = translatedX > newStockCx;
      translatedIsCorrectSide = shouldBeRight === translatedIsRight;
    } else {
      const shouldBeBottom = expectedSide === 'bottom';
      const translatedIsBottom = translatedY > newStockCy;
      translatedIsCorrectSide = shouldBeBottom === translatedIsBottom;
    }

    if (translatedIsCorrectSide) {
      // Translation keeps the endpoint on the correct side - use translated position
      // This handles normal movement where the edge offset is preserved
      newCloudX = translatedX;
      newCloudY = translatedY;
    } else {
      // Translation would put endpoint on wrong side - recompute the edge
      // This handles reattachment to a stock on the opposite side of the corner
      const stockEdge = getStockEdgePoint(newStockCx, newStockCy, expectedSide);
      newCloudX = stockEdge.x;
      newCloudY = stockEdge.y;
    }
  } else {
    // For clouds: calculate position from the existing endpoint position (which is on the
    // stock edge for stock-attached endpoints), not from cloud.x/y (stock center).
    // This ensures that dragging a stock-attached endpoint keeps it on the edge.
    newCloudX = cloudPoint.x - moveDelta.x;
    newCloudY = cloudPoint.y - moveDelta.y;
  }

  // Update adjacent corner to maintain orthogonality
  let newAdjacentX = adjacentPoint.x;
  let newAdjacentY = adjacentPoint.y;

  if (adjacentSegment.isHorizontal) {
    // Horizontal segment: cloud Y change affects corner Y
    newAdjacentY = newCloudY;
  } else if (adjacentSegment.isVertical) {
    // Vertical segment: cloud X change affects corner X
    newAdjacentX = newCloudX;
  }

  const newCloudPoint: Point = {
    ...cloudPoint,
    x: newCloudX,
    y: newCloudY,
  };

  const newAdjacentPoint: Point = {
    x: newAdjacentX,
    y: newAdjacentY,
    attachedToUid: adjacentPoint.attachedToUid,
  };

  if (cloudIsFirst) {
    points = arrayWith(arrayWith(points, 0, newCloudPoint), 1, newAdjacentPoint);
  } else {
    points = arrayWith(arrayWith(points, points.length - 1, newCloudPoint), points.length - 2, newAdjacentPoint);
  }

  // Update cloud position
  cloud = {
    ...cloud,
    x: newCloudX,
    y: newCloudY,
  };

  // Preserve valve fractional position on the cloud-adjacent segment
  const oldSegments = getSegments(flow.points);
  const valveOldSegment = findClosestSegment(currentValve, oldSegments);
  const cloudAdjacentSegmentIndex = cloudIsFirst ? 0 : oldSegments.length - 1;
  const valveIsOnCloudAdjacentSegment = valveOldSegment.index === cloudAdjacentSegmentIndex;

  // Normalize to remove any colinear or zero-length segments
  points = normalizeFlowPoints(points);
  const newSegments = getSegments(points);

  let newValve: IPoint;
  if (valveIsOnCloudAdjacentSegment && newSegments.length > 0) {
    const newCloudAdjacentSegmentIndex = cloudIsFirst ? 0 : newSegments.length - 1;
    const newCloudAdjacentSegment = newSegments[newCloudAdjacentSegmentIndex];
    newValve = preserveValveFraction(currentValve, valveOldSegment, newCloudAdjacentSegment);
  } else {
    const closestSegment = findClosestSegment(currentValve, newSegments);
    newValve = clampToSegment(currentValve, closestSegment);
  }

  flow = {
    ...flow,
    x: newValve.x,
    y: newValve.y,
    points,
  };

  return [cloud, flow];
}

export interface Segment {
  index: number;
  p1: IPoint;
  p2: IPoint;
  isHorizontal: boolean;
  isVertical: boolean;
  isDiagonal: boolean;
}

// Exported for testing
export function getSegments(points: readonly Point[]): Segment[] {
  const segments: Segment[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    const p1 = at(points, i);
    const p2 = at(points, i + 1);
    const isHorizontal = p1.y === p2.y;
    const isVertical = p1.x === p2.x;
    const isDiagonal = !isHorizontal && !isVertical;
    segments.push({
      index: i,
      p1: { x: p1.x, y: p1.y },
      p2: { x: p2.x, y: p2.y },
      isHorizontal,
      isVertical,
      isDiagonal,
    });
  }
  return segments;
}

/**
 * Normalizes flow points to ensure valid geometry:
 *
 * 1. Segments must alternate between horizontal and vertical - no two adjacent
 *    segments should have the same orientation (colinear segments).
 * 2. No zero-length segments - adjacent points at the same position are redundant.
 *
 * This function removes interior points that violate these rules. The first and
 * last points are always preserved since they're attached to stocks/clouds.
 *
 * Examples of normalization:
 * - [A, B, C] where A-B and B-C are both horizontal → [A, C] (B is redundant)
 * - [A, B, C] where B is at the same position as A → [A, C] (B is redundant)
 *
 * This should be called after any operation that modifies flow points (stock moves,
 * segment drags) to maintain clean geometry.
 */
export function normalizeFlowPoints(points: readonly Point[]): readonly Point[] {
  // Need at least 2 points for a valid flow
  if (points.length <= 2) {
    return points;
  }

  // First and last points are attached to stocks/clouds - always preserve them
  const firstPoint = first(points);
  const lastPoint = last(points);

  const result: Point[] = [firstPoint];

  for (let i = 1; i < points.length - 1; i++) {
    const prev = last(result);
    const curr = at(points, i);
    const next = at(points, i + 1);

    // Skip if this point creates a zero-length segment with prev
    if (prev.x === curr.x && prev.y === curr.y) {
      continue;
    }

    // Skip if this point creates a zero-length segment with next
    if (curr.x === next.x && curr.y === next.y) {
      continue;
    }

    // Check if prev-curr and curr-next are colinear (same orientation)
    const prevToCurrIsHorizontal = prev.y === curr.y;
    const prevToCurrIsVertical = prev.x === curr.x;
    const currToNextIsHorizontal = curr.y === next.y;
    const currToNextIsVertical = curr.x === next.x;

    // Skip if both segments are horizontal or both are vertical (colinear)
    if ((prevToCurrIsHorizontal && currToNextIsHorizontal) || (prevToCurrIsVertical && currToNextIsVertical)) {
      continue;
    }

    result.push(curr);
  }

  // Always add the last point
  result.push(lastPoint);

  return result;
}

// General point-to-line-segment distance using vector projection
function distanceToSegmentGeneral(point: IPoint, p1: IPoint, p2: IPoint): number {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const lenSq = dx * dx + dy * dy;

  // Degenerate segment (single point)
  if (lenSq === 0) {
    return Math.hypot(point.x - p1.x, point.y - p1.y);
  }

  // Parameter t of the closest point on the infinite line
  let t = ((point.x - p1.x) * dx + (point.y - p1.y) * dy) / lenSq;
  // Clamp t to [0, 1] to stay on the segment
  t = Math.max(0, Math.min(1, t));

  // Closest point on segment
  const closestX = p1.x + t * dx;
  const closestY = p1.y + t * dy;

  return Math.hypot(point.x - closestX, point.y - closestY);
}

function distanceToSegment(point: IPoint, seg: Segment): number {
  const { p1, p2 } = seg;

  // For diagonal segments, use the general formula
  if (seg.isDiagonal) {
    return distanceToSegmentGeneral(point, p1, p2);
  }

  if (seg.isHorizontal) {
    const minX = Math.min(p1.x, p2.x);
    const maxX = Math.max(p1.x, p2.x);
    if (point.x >= minX && point.x <= maxX) {
      return Math.abs(point.y - p1.y);
    }
    const distToP1 = Math.hypot(point.x - p1.x, point.y - p1.y);
    const distToP2 = Math.hypot(point.x - p2.x, point.y - p2.y);
    return Math.min(distToP1, distToP2);
  } else {
    // Vertical segment
    const minY = Math.min(p1.y, p2.y);
    const maxY = Math.max(p1.y, p2.y);
    if (point.y >= minY && point.y <= maxY) {
      return Math.abs(point.x - p1.x);
    }
    const distToP1 = Math.hypot(point.x - p1.x, point.y - p1.y);
    const distToP2 = Math.hypot(point.x - p2.x, point.y - p2.y);
    return Math.min(distToP1, distToP2);
  }
}

export function findClosestSegment(point: IPoint, segments: Segment[]): Segment {
  if (segments.length === 0) {
    throw new Error('findClosestSegment called with empty segments array');
  }
  let closest = segments[0];
  let minDist = distanceToSegment(point, closest);
  for (let i = 1; i < segments.length; i++) {
    const dist = distanceToSegment(point, segments[i]);
    if (dist < minDist) {
      minDist = dist;
      closest = segments[i];
    }
  }
  return closest;
}

export function clampToSegment(point: IPoint, seg: Segment, margin: number = VALVE_CLAMP_MARGIN): IPoint {
  const { p1, p2 } = seg;

  // For diagonal segments, project onto the line and apply margin
  if (seg.isDiagonal) {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const len = Math.hypot(dx, dy);

    if (len === 0) {
      return { x: p1.x, y: p1.y };
    }

    // If segment is shorter than 2 * margin, use the midpoint
    if (len < 2 * margin) {
      return {
        x: (p1.x + p2.x) / 2,
        y: (p1.y + p2.y) / 2,
      };
    }

    // Parameter t of the closest point on the infinite line
    let t = ((point.x - p1.x) * dx + (point.y - p1.y) * dy) / (len * len);

    // Clamp t to [margin/len, 1 - margin/len] to apply margin from endpoints
    const marginT = margin / len;
    t = Math.max(marginT, Math.min(1 - marginT, t));

    return {
      x: p1.x + t * dx,
      y: p1.y + t * dy,
    };
  }

  if (seg.isHorizontal) {
    const segLen = Math.abs(p2.x - p1.x);
    // If segment is shorter than 2 * margin, use the midpoint
    if (segLen < 2 * margin) {
      return {
        x: (p1.x + p2.x) / 2,
        y: p1.y,
      };
    }
    const minX = Math.min(p1.x, p2.x) + margin;
    const maxX = Math.max(p1.x, p2.x) - margin;
    return {
      x: Math.max(minX, Math.min(maxX, point.x)),
      y: p1.y,
    };
  } else {
    // Vertical segment
    const segLen = Math.abs(p2.y - p1.y);
    // If segment is shorter than 2 * margin, use the midpoint
    if (segLen < 2 * margin) {
      return {
        x: p1.x,
        y: (p1.y + p2.y) / 2,
      };
    }
    const minY = Math.min(p1.y, p2.y) + margin;
    const maxY = Math.max(p1.y, p2.y) - margin;
    return {
      x: p1.x,
      y: Math.max(minY, Math.min(maxY, point.y)),
    };
  }
}

const VALVE_RADIUS = 6;
const VALVE_HIT_TOLERANCE = 5;
// Margin from segment endpoints when clamping valve position
const VALVE_CLAMP_MARGIN = 10;

/**
 * Snap a valve onto the closest segment of a flow path -- the recurring
 * getSegments -> findClosestSegment -> clampToSegment chain run wherever a
 * flow's geometry changes (stock/cloud moves, segment drags, L-shape
 * conversions). Assumes `points` has at least two points, which every caller
 * guarantees, so `findClosestSegment` always has a segment to return.
 */
function clampValveToClosestSegment(valve: IPoint, points: readonly Point[]): IPoint {
  const segments = getSegments(points);
  return clampToSegment(valve, findClosestSegment(valve, segments));
}

/**
 * Preserves the valve's fractional position when a segment changes.
 *
 * When a stock moves along the flow axis, the segment gets longer or shorter.
 * Instead of just clamping the valve to the new segment bounds (which causes it
 * to jump to an endpoint when the stock moves past the valve), we preserve
 * the valve's proportional position along the segment.
 *
 * For example, if the valve was at 65% from the anchor toward the stock on the
 * old segment, it will be placed at 65% from the anchor toward the stock on the
 * new segment.
 *
 * @param valve The current valve position
 * @param oldSeg The segment before the stock moved
 * @param newSeg The segment after the stock moved
 * @param margin Minimum distance from segment endpoints
 * @returns The new valve position preserving fractional placement
 */
function preserveValveFraction(valve: IPoint, oldSeg: Segment, newSeg: Segment): IPoint {
  if (oldSeg.isHorizontal && newSeg.isHorizontal) {
    const oldLen = oldSeg.p2.x - oldSeg.p1.x;
    const newLen = newSeg.p2.x - newSeg.p1.x;

    // Calculate valve's fraction along old segment (0 = at p1, 1 = at p2)
    // Don't apply margin constraints - preserve the exact fractional position
    // as the adjustFlows logic does. This prevents the valve from jumping
    // when the stock moves past it.
    let fraction = oldLen !== 0 ? (valve.x - oldSeg.p1.x) / oldLen : 0.5;

    // Only clamp to [0, 1] to ensure the valve stays on the segment
    fraction = Math.max(0, Math.min(1, fraction));

    return {
      x: newSeg.p1.x + fraction * newLen,
      y: newSeg.p1.y,
    };
  } else if (oldSeg.isVertical && newSeg.isVertical) {
    const oldLen = oldSeg.p2.y - oldSeg.p1.y;
    const newLen = newSeg.p2.y - newSeg.p1.y;

    // Calculate valve's fraction along old segment
    let fraction = oldLen !== 0 ? (valve.y - oldSeg.p1.y) / oldLen : 0.5;

    // Only clamp to [0, 1] to ensure the valve stays on the segment
    fraction = Math.max(0, Math.min(1, fraction));

    return {
      x: newSeg.p1.x,
      y: newSeg.p1.y + fraction * newLen,
    };
  }

  // For mixed orientation or diagonal segments, fall back to clamping
  return clampToSegment(valve, newSeg);
}

// Check if a segment has an attached endpoint that would prevent dragging.
// Dragging a segment with an attached endpoint would create a diagonal segment,
// which breaks the axis-alignment assumptions used by hit-testing and valve clamping.
function segmentHasAttachedEndpoint(points: readonly Point[], segmentIndex: number): boolean {
  const numSegments = points.length - 1;
  if (segmentIndex < 0 || segmentIndex >= numSegments) {
    return true;
  }

  // Check both endpoints of the segment
  const p1 = points[segmentIndex];
  const p2 = points[segmentIndex + 1];

  return p1?.attachedToUid !== undefined || p2?.attachedToUid !== undefined;
}

// Determine which segment was clicked, or undefined if clicking on the valve
export function findClickedSegment(
  clickX: number,
  clickY: number,
  valveCx: number,
  valveCy: number,
  points: readonly Point[],
): number | undefined {
  // If click is on/near the valve, return undefined (valve drag, not segment)
  const distToValve = Math.hypot(clickX - valveCx, clickY - valveCy);
  if (distToValve <= VALVE_RADIUS + VALVE_HIT_TOLERANCE) {
    return undefined;
  }

  const segments = getSegments(points);
  if (segments.length === 0) {
    return undefined;
  }

  // For single-segment flows (straight lines), clicking anywhere drags the valve
  if (segments.length === 1) {
    return undefined;
  }

  // For multi-segment flows, find closest segment
  const clickPoint: IPoint = { x: clickX, y: clickY };
  const closest = findClosestSegment(clickPoint, segments);

  // Don't allow dragging diagonal segments - they shouldn't exist in valid
  // flow geometry, but could appear in imported models. moveSegment assumes
  // all segments are axis-aligned, so dragging a diagonal would be incorrect.
  if (closest.isDiagonal) {
    return undefined;
  }

  // Don't allow dragging segments that have an attached endpoint
  // (would create diagonal segments which break axis-alignment assumptions)
  if (segmentHasAttachedEndpoint(points, closest.index)) {
    return undefined;
  }

  return closest.index;
}

// Move a segment perpendicular to its direction, adjusting adjacent segments
export function moveSegment(points: readonly Point[], segmentIndex: number, delta: IPoint): readonly Point[] {
  const segments = getSegments(points);
  if (segmentIndex < 0 || segmentIndex >= segments.length) {
    return points;
  }

  const seg = segments[segmentIndex];
  const isFirst = segmentIndex === 0;
  const isLast = segmentIndex === segments.length - 1;

  return points.map((p, i) => {
    if (seg.isHorizontal) {
      // Horizontal segment: move up/down (change Y)
      // Both endpoints of this segment move
      if (i === segmentIndex || i === segmentIndex + 1) {
        // Don't move attached endpoints (first and last points)
        if ((i === 0 && isFirst) || (i === points.length - 1 && isLast)) {
          return p;
        }
        return { ...p, y: p.y - delta.y };
      }
    } else {
      // Vertical segment: move left/right (change X)
      if (i === segmentIndex || i === segmentIndex + 1) {
        if ((i === 0 && isFirst) || (i === points.length - 1 && isLast)) {
          return p;
        }
        return { ...p, x: p.x - delta.x };
      }
    }
    return p;
  });
}

export function UpdateFlow(
  flowEl: FlowViewElement,
  ends: readonly (StockViewElement | CloudViewElement)[],
  moveDelta: IPoint,
  segmentIndex?: number,
): [FlowViewElement, readonly CloudViewElement[]] {
  const clouds = ends.filter((e): e is CloudViewElement => e.type === 'cloud');

  let points = flowEl.points;

  const currentValve: IPoint = { x: flowEl.x, y: flowEl.y };
  const proposedValve: IPoint = {
    x: currentValve.x - moveDelta.x,
    y: currentValve.y - moveDelta.y,
  };

  // For cloud-to-cloud flows, move everything uniformly
  const hasStock = ends.some((e) => e.type === 'stock');
  if (!hasStock) {
    points = points.map((p) => ({ ...p, x: p.x - moveDelta.x, y: p.y - moveDelta.y }));
    flowEl = {
      ...flowEl,
      x: proposedValve.x,
      y: proposedValve.y,
      points,
    };

    const updatedClouds: readonly CloudViewElement[] = clouds.map((cloud) => {
      return {
        ...cloud,
        x: cloud.x - moveDelta.x,
        y: cloud.y - moveDelta.y,
      };
    });

    return [flowEl, updatedClouds];
  }

  const segments = getSegments(points);

  // If a specific segment is being moved, move that segment.
  // Note: We return an empty clouds list because segment movement only affects
  // interior points (corners), not attached endpoints. Attached endpoints stay
  // fixed at their stock/cloud positions. Only draggable segments are interior
  // segments (between two corners), so no cloud positions need updating.
  if (segmentIndex !== undefined) {
    points = moveSegment(points, segmentIndex, moveDelta);

    // Normalize to remove any colinear or zero-length segments that may have
    // been created by the segment movement.
    points = normalizeFlowPoints(points);

    // Always re-clamp the valve to the closest segment after any segment drag.
    // Dragging any segment can affect adjacent segments via shared corners,
    // so the valve's segment may have changed shape even if it wasn't the
    // segment being dragged.
    const newValve = clampValveToClosestSegment(currentValve, points);
    flowEl = {
      ...flowEl,
      x: newValve.x,
      y: newValve.y,
      points,
    };

    return [flowEl, []];
  }

  // For 2-point (straight) flows, allow a perpendicular valve drag to offset the
  // route. A flow with a cloud endpoint bends into an L (the cloud moves); a flow
  // pinned between two stocks -- where neither endpoint can move -- inserts two
  // corners to form a Z whose middle segment runs parallel to the original axis,
  // displaced by the perpendicular drag (#819). Once either shape exists, its
  // middle/bent segment is interior, so the normal segment-drag machinery takes
  // over and dragging it back onto the axis collapses (via normalizeFlowPoints)
  // to the straight flow.
  if (segments.length === 1) {
    const seg = segments[0];
    // Classify by the dominant axis rather than exact equality so a
    // slightly-diagonal legacy flow is offset along the axis it visually runs
    // (audit finding 5); for exact orthogonal flows this matches seg.isHorizontal.
    const treatAsHorizontal = isDominantlyHorizontal(seg.p1, seg.p2);
    const perpDelta = treatAsHorizontal ? moveDelta.y : moveDelta.x;
    const parDelta = treatAsHorizontal ? moveDelta.x : moveDelta.y;

    // Require a significant and dominant perpendicular component to trigger reroute.
    // This prevents accidental reroute during normal valve dragging, since pointer
    // movement often has small perpendicular noise.
    const PERP_THRESHOLD = 5;
    const perpAbs = Math.abs(perpDelta);
    const parAbs = Math.abs(parDelta);
    const shouldReroute = perpAbs >= PERP_THRESHOLD && perpAbs > parAbs;

    if (shouldReroute && clouds.length > 0) {
      const firstPoint = first(points);
      const lastPoint = last(points);

      // Find which endpoint is the cloud vs stock
      const firstIsCloud = clouds.some((c) => c.uid === firstPoint.attachedToUid);
      const lastIsCloud = clouds.some((c) => c.uid === lastPoint.attachedToUid);

      // Calculate new cloud position(s)
      let newFirstPoint = firstPoint;
      let newLastPoint = lastPoint;

      if (treatAsHorizontal) {
        // Horizontal segment: perpendicular movement is vertical (Y changes)
        if (firstIsCloud) {
          newFirstPoint = { ...firstPoint, y: firstPoint.y - moveDelta.y };
        }
        if (lastIsCloud) {
          newLastPoint = { ...lastPoint, y: lastPoint.y - moveDelta.y };
        }

        // Create corner point to maintain orthogonality
        // Corner connects the moved cloud to the fixed stock
        let corner: Point;
        if (firstIsCloud && !lastIsCloud) {
          // Cloud is first, stock is last: corner at (stock.x, newCloud.y)
          corner = { x: lastPoint.x, y: newFirstPoint.y, attachedToUid: undefined };
          points = [newFirstPoint, corner, lastPoint];
        } else if (!firstIsCloud && lastIsCloud) {
          // Stock is first, cloud is last: corner at (stock.x, newCloud.y)
          corner = { x: firstPoint.x, y: newLastPoint.y, attachedToUid: undefined };
          points = [firstPoint, corner, newLastPoint];
        } else {
          // Both are clouds - move both endpoints uniformly
          points = [newFirstPoint, newLastPoint];
        }
      } else {
        // Vertical segment: perpendicular movement is horizontal (X changes)
        if (firstIsCloud) {
          newFirstPoint = { ...firstPoint, x: firstPoint.x - moveDelta.x };
        }
        if (lastIsCloud) {
          newLastPoint = { ...lastPoint, x: lastPoint.x - moveDelta.x };
        }

        // Create corner point
        let corner: Point;
        if (firstIsCloud && !lastIsCloud) {
          // Cloud is first, stock is last: corner at (newCloud.x, stock.y)
          corner = { x: newFirstPoint.x, y: lastPoint.y, attachedToUid: undefined };
          points = [newFirstPoint, corner, lastPoint];
        } else if (!firstIsCloud && lastIsCloud) {
          // Stock is first, cloud is last: corner at (newCloud.x, stock.y)
          corner = { x: newLastPoint.x, y: firstPoint.y, attachedToUid: undefined };
          points = [firstPoint, corner, newLastPoint];
        } else {
          // Both are clouds - move both endpoints uniformly
          points = [newFirstPoint, newLastPoint];
        }
      }

      // Update cloud positions
      const updatedClouds: readonly CloudViewElement[] = clouds.map((cloud) => {
        if (cloud.uid === firstPoint.attachedToUid && firstIsCloud) {
          return {
            ...cloud,
            x: newFirstPoint.x,
            y: newFirstPoint.y,
          };
        } else if (cloud.uid === lastPoint.attachedToUid && lastIsCloud) {
          return {
            ...cloud,
            x: newLastPoint.x,
            y: newLastPoint.y,
          };
        }
        return cloud;
      });

      // Clamp valve to the closest segment of the new shape
      const newValve = clampValveToClosestSegment(currentValve, points);

      flowEl = {
        ...flowEl,
        x: newValve.x,
        y: newValve.y,
        points,
      };

      return [flowEl, updatedClouds];
    }

    if (shouldReroute && clouds.length === 0) {
      // Both endpoints are attached stocks, so neither can move to absorb the
      // offset. Keep both endpoints on their current stock edges and insert two
      // corners at the displaced perpendicular coordinate (the dragged valve's
      // position), forming a Z: two perpendicular risers bracketing a middle
      // segment parallel to the original axis. The endpoints are untouched, so
      // corner clearance is preserved and dragging the middle back onto the axis
      // collapses the risers to zero length (normalizeFlowPoints) -> straight.
      const firstPoint = first(points);
      const lastPoint = last(points);

      let corner1: Point;
      let corner2: Point;
      if (treatAsHorizontal) {
        // Horizontal flow: risers are vertical (on each endpoint's X), the
        // middle segment is horizontal at the dragged valve's Y.
        const midY = proposedValve.y;
        corner1 = { x: firstPoint.x, y: midY, attachedToUid: undefined };
        corner2 = { x: lastPoint.x, y: midY, attachedToUid: undefined };
      } else {
        // Vertical flow: risers are horizontal (on each endpoint's Y), the
        // middle segment is vertical at the dragged valve's X.
        const midX = proposedValve.x;
        corner1 = { x: midX, y: firstPoint.y, attachedToUid: undefined };
        corner2 = { x: midX, y: lastPoint.y, attachedToUid: undefined };
      }

      // Normalize like every other mutation path so the orthogonality invariant
      // doesn't silently rely on the perpendicular threshold keeping the risers
      // non-degenerate.
      points = normalizeFlowPoints([firstPoint, corner1, corner2, lastPoint]);

      // Land the valve on the interior (middle) segment the drag created, not
      // merely the closest segment: a perpendicular-dominant drag can still
      // carry a large parallel component, which can leave a riser closer to the
      // dragged position and strand the valve off the offset the user just made.
      const zSegments = getSegments(points);
      const middleSeg = zSegments.length === 3 ? zSegments[1] : findClosestSegment(proposedValve, zSegments);
      const newValve = clampToSegment(proposedValve, middleSeg);
      flowEl = {
        ...flowEl,
        x: newValve.x,
        y: newValve.y,
        points,
      };

      return [flowEl, []];
    }
  }

  // Moving the valve along the flow path.
  // Note: Valve movement doesn't change any endpoint positions, so no cloud
  // positions need updating. We return an empty clouds list.
  // Use proposedValve (not currentValve) to find the closest segment, so the
  // valve can cross corners when dragged past them to another segment.
  const closestSegment = findClosestSegment(proposedValve, segments);
  const clampedValve = clampToSegment(proposedValve, closestSegment);

  flowEl = {
    ...flowEl,
    x: clampedValve.x,
    y: clampedValve.y,
  };

  return [flowEl, []];
}

/**
 * Pull a cloud-terminated flow's endpoint back by `radius` along the
 * direction of the final segment, so the arrowhead lands on the cloud's
 * edge rather than at its center. The retraction follows the segment's unit
 * vector: for orthogonal segments this matches a single-axis shift, while a
 * diagonal segment retracts by exactly `radius` (applying independent x and
 * y shifts would over-retract by sqrt(2)*radius). A zero-length final
 * segment is returned unchanged.
 */
export function retractFinalPointIntoCloud(pts: readonly Point[], radius: number): readonly Point[] {
  const lastPt = at(pts, pts.length - 1);
  const prevPt = at(pts, pts.length - 2);
  const dx = lastPt.x - prevPt.x;
  const dy = lastPt.y - prevPt.y;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len === 0) {
    return pts;
  }
  return arrayWith(pts, pts.length - 1, {
    ...lastPt,
    x: lastPt.x - (radius * dx) / len,
    y: lastPt.y - (radius * dy) / len,
  });
}

/**
 * Direction, in degrees [0, 360), of the last non-degenerate segment ending
 * at the flow's final point. Walks backward past coincident points --
 * atan2(0, 0) is 0, which would otherwise read a zero-length final segment
 * as "pointing right". Returns undefined when every point coincides.
 */
export function finalSegmentAngle(pts: readonly Point[]): number | undefined {
  const lastPt = at(pts, pts.length - 1);
  for (let i = pts.length - 2; i >= 0; i--) {
    const p = at(pts, i);
    const dx = lastPt.x - p.x;
    const dy = lastPt.y - p.y;
    if (dx !== 0 || dy !== 0) {
      let theta = (Math.atan2(dy, dx) * 180) / Math.PI;
      if (theta < 0) {
        theta += 360;
      }
      return theta;
    }
  }
  return undefined;
}

export function flowBounds(element: FlowViewElement): Rect {
  const cx = element.x;
  const cy = element.y;
  // Flow valve is a circle with radius 6 (FlowWidth/2 = 12/2 = 6)
  const r = 6;
  const bounds = {
    top: cy - r,
    left: cx - r,
    right: cx + r,
    bottom: cy + r,
  };

  // Include label bounds if there's a label
  if (element.name) {
    const side = element.labelSide;
    const labelProps: LabelProps = {
      cx,
      cy,
      side,
      rw: r,
      rh: r,
      text: displayName(element.name),
    };
    const lBounds = labelBounds(labelProps);

    bounds.top = Math.min(bounds.top, lBounds.top);
    bounds.left = Math.min(bounds.left, lBounds.left);
    bounds.right = Math.max(bounds.right, lBounds.right);
    bounds.bottom = Math.max(bounds.bottom, lBounds.bottom);
  }

  // Also include flow path points
  if (element.points) {
    for (const point of element.points) {
      bounds.left = Math.min(bounds.left, point.x);
      bounds.right = Math.max(bounds.right, point.x);
      bounds.top = Math.min(bounds.top, point.y);
      bounds.bottom = Math.max(bounds.bottom, point.y);
    }
  }

  return bounds;
}

export interface FlowProps {
  isSelected: boolean;
  isEditingName: boolean;
  isValidTarget?: boolean;
  isMovingArrow: boolean;
  isMovingSource: boolean;
  hasWarning?: boolean;
  embedded?: boolean;
  series: Readonly<Array<Series>> | undefined;
  onSelection: (
    el: ViewElement,
    e: React.PointerEvent<SVGElement>,
    isText?: boolean,
    isArrowhead?: boolean,
    segmentIndex?: number,
    isSource?: boolean,
  ) => void;
  onLabelDrag: (uid: number, e: React.PointerEvent<SVGElement>) => void;
  source: StockViewElement | CloudViewElement;
  element: FlowViewElement;
  sink: StockViewElement | CloudViewElement;
}

export const Flow = React.memo(function Flow(props: FlowProps): React.ReactElement {
  const { element, isEditingName, isMovingArrow, isMovingSource, isSelected, isValidTarget, series, sink } = props;
  const { hasWarning, embedded, onSelection, onLabelDrag } = props;

  const handlePointerUp = (_e: React.PointerEvent<SVGElement>): void => {
    // e.preventDefault();
    // e.stopPropagation();
  };

  const handlePointerDown = (e: React.PointerEvent<SVGElement>): void => {
    e.preventDefault();
    e.stopPropagation();

    // Convert screen coordinates to model coordinates using the element's CTM.
    // We must use the clicked element's CTM (not the SVG root's CTM) because
    // Canvas applies zoom/pan via a parent <g transform="matrix(...)"> group.
    // The element's CTM includes this transform, so inverting it correctly
    // converts screen coordinates to model coordinates regardless of zoom/pan.
    const target = e.currentTarget as SVGGraphicsElement;
    const svg = target.ownerSVGElement;
    let segmentIndex: number | undefined;

    if (svg) {
      const pt = svg.createSVGPoint();
      pt.x = e.clientX;
      pt.y = e.clientY;
      const ctm = target.getScreenCTM();
      if (ctm) {
        const modelPt = pt.matrixTransform(ctm.inverse());
        segmentIndex = findClickedSegment(modelPt.x, modelPt.y, element.x, element.y, element.points);
      }
    }

    onSelection(element, e, false, false, segmentIndex);
  };

  // Memoized: passed to the memo'd Label/Arrowhead below, so a stable identity
  // (while element/onSelection are unchanged) lets them skip re-rendering.
  const handleLabelSelection = React.useCallback(
    (e: React.PointerEvent<SVGElement>): void => {
      e.preventDefault();
      e.stopPropagation();
      onSelection(element, e, true);
    },
    [onSelection, element],
  );

  const handlePointerDownArrowhead = React.useCallback(
    (e: React.PointerEvent<SVGElement>): void => {
      e.preventDefault();
      e.stopPropagation();
      onSelection(element, e, false, true);
    },
    [onSelection, element],
  );

  const handlePointerDownSource = (e: React.PointerEvent<SVGElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    onSelection(element, e, false, false, undefined, true);
  };

  const isArrayed = element.var ? variableIsArrayed(element.var) : false;
  const arrayedOffset = isArrayed ? 3 : 0;

  let pts = element.points;
  if (pts.length < 2) {
    throw new Error('expected at least two points on a flow');
  }

  if (sink.type === 'cloud' && !isMovingArrow) {
    pts = retractFinalPointIntoCloud(pts, CloudRadius);
  }

  const finalAdjust = 7.5;
  let spath = '';
  let arrowTheta = 0;
  for (let j = 0; j < pts.length; j++) {
    let x = at(pts, j).x;
    let y = at(pts, j).y;
    if (j === pts.length - 1) {
      // Walk back past coincident points: a degenerate (zero-length) final
      // segment must not read as "pointing right" via atan2(0, 0) === 0.
      const theta = finalSegmentAngle(pts);
      if (theta === undefined) {
        arrowTheta = 0;
      } else if (theta >= 315 || theta < 45) {
        x -= finalAdjust;
        arrowTheta = 0;
      } else if (theta >= 45 && theta < 135) {
        y -= finalAdjust;
        arrowTheta = 90;
      } else if (theta >= 135 && theta < 225) {
        x += finalAdjust;
        arrowTheta = 180;
      } else {
        y += finalAdjust;
        arrowTheta = 270;
      }
    }
    const prefix = j === 0 ? 'M' : 'L';
    // Quantize flow path coordinates -- see `jsFormatNumber` in
    // `render-common.tsx` for the cross-toolchain SVG parity invariant.
    spath += `${prefix}${ff(x)},${ff(y)}`;
  }

  const cx = element.x;
  const cy = element.y;
  const r = AuxRadius;

  const lastPt = at(pts, pts.length - 1);
  const side = element.labelSide;
  const label = isEditingName ? undefined : (
    <Label
      uid={element.uid}
      cx={cx}
      cy={cy}
      side={side}
      rw={r + arrayedOffset}
      rh={r + arrayedOffset}
      text={displayName(defined(element.name))}
      onSelection={handleLabelSelection}
      onLabelDrag={onLabelDrag}
    />
  );

  let sparkline;
  if (series && series.length > 0) {
    const sx = cx - arrayedOffset;
    const sy = cy - arrayedOffset;
    sparkline = (
      <g transform={`translate(${ff(sx + 1 - r / 2)} ${ff(sy + 1 - r / 2)})`}>
        <Sparkline series={series} width={r - 2} height={r - 2} />
      </g>
    );
  }

  let indicator;
  if (hasWarning) {
    const theta = -Math.PI / 4; // 45 degrees
    indicator = (
      <circle className={styles.errorIndicator} cx={cx + r * Math.cos(theta)} cy={cy + r * Math.sin(theta)} r={3} />
    );
  }

  const groupClassName = clsx(styles.flow, 'simlin-flow', {
    [styles.selected]: isSelected && isValidTarget === undefined,
    'simlin-selected': isSelected && isValidTarget === undefined,
    [styles.targetGood]: isValidTarget === true,
    [styles.targetBad]: isValidTarget === false,
  });

  let circles = [<circle key="1" cx={cx} cy={cy} r={r} />];
  if (isArrayed) {
    circles = [
      <circle key="0" cx={cx + arrayedOffset} cy={cy + arrayedOffset} r={r} />,
      <circle key="1" cx={cx} cy={cy} r={r} />,
      <circle key="2" cx={cx - arrayedOffset} cy={cy - arrayedOffset} r={r} />,
    ];
  }

  const outerClassName = isSelected
    ? clsx(styles.outerSelected, 'simlin-outer-selected')
    : clsx(styles.outer, 'simlin-outer');

  // Invisible hit area at the source end for grabbing the source
  // Position it slightly into the first segment from the source point
  const firstPt = at(pts, 0);
  const secondPt = at(pts, 1);
  const sourceHitSize = 20;

  // Calculate position along the first segment, offset from the source
  let sourceHitX = firstPt.x;
  let sourceHitY = firstPt.y;

  // Move hit area slightly into the segment for better UX
  const segDx = secondPt.x - firstPt.x;
  const segDy = secondPt.y - firstPt.y;
  const segLen = Math.hypot(segDx, segDy);
  if (segLen > sourceHitSize) {
    const offsetRatio = sourceHitSize / 2 / segLen;
    sourceHitX = firstPt.x + segDx * offsetRatio;
    sourceHitY = firstPt.y + segDy * offsetRatio;
  }

  // Only show the source hit area when not in embedded/export mode and not already moving the source
  const sourceHitArea =
    !embedded && !isMovingSource ? (
      <rect
        x={sourceHitX - sourceHitSize / 2}
        y={sourceHitY - sourceHitSize / 2}
        width={sourceHitSize}
        height={sourceHitSize}
        fill="transparent"
        style={{ cursor: 'grab' }}
        onPointerDown={handlePointerDownSource}
      />
    ) : null;

  return (
    <g className={groupClassName}>
      <path d={spath} className={outerClassName} onPointerDown={handlePointerDown} onPointerUp={handlePointerUp} />
      {sourceHitArea}
      <Arrowhead
        point={lastPt}
        angle={arrowTheta}
        size={FlowArrowheadRadius}
        type="flow"
        isSelected={isSelected}
        onSelection={handlePointerDownArrowhead}
      />
      <path d={spath} className={clsx(styles.inner, 'simlin-inner')} />
      <g onPointerDown={handlePointerDown} onPointerUp={handlePointerUp}>
        {circles}
        {sparkline}
      </g>
      {indicator}
      {label}
    </g>
  );
});
